// src/components/AliasFormModal.tsx
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { AppDrawer } from '@/components/ui/AppDrawer';

// Mirrors the worker's merchantDedupKey loosely — we only need it client-side
// to compute "is the source_text I'm typing going to collide with an existing
// alias's source_key" and to find related rules by shared tokens. The worker
// is still the source of truth at insert time.
function clientDedupKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/[\\/]+/g, ' ')
    .replace(/\s+\d+\b$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(s: string): Set<string> {
  return new Set(
    s
      .toLowerCase()
      .split(/[\s\-_/]+/)
      .map((t) => t.replace(/[^\w一-鿿]/g, ''))
      .filter((t) => t.length >= 2),
  );
}

export interface AliasRow {
  id: string;
  workspace_id: string;
  source_key: string;
  display_name: string;
  display_name_zh: string | null;
  default_category_id: string | null;
  apply_count: number;
  last_applied_at: number | null;
  created_at: number;
  updated_at: number;
}

interface CategoryOption {
  id: string;
  name: string;
  name_zh: string | null;
  icon: string | null;
}

interface PreviewResponse {
  source_key: string;
  count: number;
  samples: Array<{
    id: string;
    merchant: string | null;
    receipt_date: string | null;
    total_amount: number;
  }>;
}

export interface AliasPrefill {
  source_text?: string;
  display_name?: string;
  display_name_zh?: string | null;
  default_category_id?: string | null;
}

interface Props {
  mode: 'create' | 'edit';
  initial?: AliasRow;
  /** Pre-fills the form fields in create mode. Ignored in edit mode. */
  prefill?: AliasPrefill;
  onClose: () => void;
}

export function AliasFormModal({ mode, initial, prefill, onClose }: Props) {
  const { t } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const qc = useQueryClient();

  const [sourceText, setSourceText] = useState(
    mode === 'edit' ? (initial?.source_key ?? '') : (prefill?.source_text ?? ''),
  );
  const [displayName, setDisplayName] = useState(
    initial?.display_name ?? prefill?.display_name ?? '',
  );
  const [displayNameZh, setDisplayNameZh] = useState(
    initial?.display_name_zh ?? prefill?.display_name_zh ?? '',
  );
  const [categoryId, setCategoryId] = useState(
    initial?.default_category_id ?? prefill?.default_category_id ?? '',
  );
  const [backfill, setBackfill] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: categories } = useQuery({
    queryKey: ['categories', ws?.id],
    queryFn: () => api.get<CategoryOption[]>('/categories'),
    enabled: !!ws,
    staleTime: 5 * 60 * 1000,
  });

  // All workspace aliases, used to suggest "reuse target" when creating a new
  // rule that's similar to an existing one (e.g. user already aliased
  // "abc hornsby" → BP Fuel; now they're aliasing "abc hornsby shop" — same
  // brand, different source string — and shouldn't have to retype the target).
  const { data: allAliases } = useQuery({
    queryKey: ['aliases', ws?.id],
    queryFn: () => api.get<AliasRow[]>('/aliases'),
    enabled: !!ws && mode === 'create',
    staleTime: 60 * 1000,
  });

  // Find related aliases: share ≥1 substantive token with the current
  // source_text. Sorted by token-overlap desc, then by apply_count.
  // Exact-key match is surfaced separately as a "this rule already exists"
  // notice (since creating it would 409 on the server).
  const { exactMatch, related } = useMemo(() => {
    if (mode !== 'create' || !sourceText.trim() || !allAliases) {
      return { exactMatch: null as AliasRow | null, related: [] as AliasRow[] };
    }
    const inputKey = clientDedupKey(sourceText);
    const inputTokens = tokenize(sourceText);
    let exact: AliasRow | null = null;
    const scored: { alias: AliasRow; overlap: number }[] = [];
    for (const a of allAliases) {
      if (a.source_key === inputKey) {
        exact = a;
        continue;
      }
      const aliasTokens = tokenize(a.source_key);
      let overlap = 0;
      for (const t of inputTokens) if (aliasTokens.has(t)) overlap++;
      if (overlap >= 1) scored.push({ alias: a, overlap });
    }
    scored.sort(
      (a, b) => b.overlap - a.overlap || b.alias.apply_count - a.alias.apply_count,
    );
    return { exactMatch: exact, related: scored.slice(0, 5).map((s) => s.alias) };
  }, [mode, sourceText, allAliases]);

  // Tap a related alias to clone its target into the form. source_text is
  // left as-is so the user creates a NEW row with the typed key + reused
  // display fields — that's the whole point of the suggestion.
  function reuseTargetFrom(a: AliasRow) {
    setDisplayName(a.display_name);
    setDisplayNameZh(a.display_name_zh ?? '');
    setCategoryId(a.default_category_id ?? '');
  }

  // Debounced preview — 200ms after the source text settles.
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  useEffect(() => {
    if (!sourceText.trim()) {
      setPreview(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        if (mode === 'edit' && initial) {
          const r = await api.get<PreviewResponse>(`/aliases/${initial.id}/preview`);
          setPreview(r);
        } else {
          const r = await api.post<PreviewResponse>('/aliases/preview', {
            source_text: sourceText,
          });
          setPreview(r);
        }
      } catch {
        setPreview(null);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [sourceText, mode, initial]);

  // Backfill rewrites the merchant + category on any receipt whose
  // normalized merchant matched the rule. That cascades into every cache
  // that aggregates over receipts. Use a single predicate covering all
  // affected prefixes so the user doesn't have to restart the app to see
  // fresh totals.
  //
  //   receipts*  — list & detail (legacy `receipts`, current `receipts-v2`)
  //   insights*  — period summary by_merchant/by_category, allowance,
  //                upcoming, savings opportunity, products, …
  //   advisor*   — household-profile, goals, fixed/income lists used as
  //                inputs to plan + tax + ROI summaries
  //   goals*     — contribution pace + planner state
  //   alerts*    — category_spike + sub_unused detection re-reads receipts
  const AFFECTED_PREFIXES = ['receipts', 'insights', 'advisor', 'goals', 'alerts'];
  async function invalidateAllAggregateCaches() {
    await qc.invalidateQueries({ queryKey: ['aliases', ws?.id] });
    await qc.invalidateQueries({
      predicate: (q) => {
        if (!Array.isArray(q.queryKey)) return false;
        const head = q.queryKey[0];
        if (typeof head !== 'string') return false;
        return AFFECTED_PREFIXES.some((p) => head.startsWith(p));
      },
    });
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        display_name: displayName,
        display_name_zh: displayNameZh || null,
        default_category_id: categoryId || null,
        backfill,
      };
      if (mode === 'create') {
        return api.post<{ alias: AliasRow; backfill_updated?: number }>('/aliases', {
          source_text: sourceText,
          ...body,
        });
      }
      if (!initial) throw new Error('initial is required for edit');
      return api.patch<{ alias: AliasRow; backfill_updated?: number }>(
        `/aliases/${initial.id}`,
        body,
      );
    },
    onSuccess: async () => {
      await invalidateAllAggregateCaches();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(t('aliases.duplicateError'));
      } else {
        setError(err instanceof Error ? err.message : t('aliases.saveFailed'));
      }
    },
  });

  // When `sourceText` exactly matches an existing alias's source_key, the
  // user can't (and shouldn't) re-create the rule — but they DO need a way
  // to apply that rule to the current receipt + any other not-yet-rewritten
  // matches. This runs the existing alias's backfill, which scans
  // receipts.merchant_normalized = source_key and rewrites merchant +
  // category to the alias's targets.
  const applyExistingMutation = useMutation({
    mutationFn: async () => {
      if (!exactMatch) throw new Error('no exact match to apply');
      return api.post<{ updated: number }>(`/aliases/${exactMatch.id}/backfill`, {});
    },
    onSuccess: async () => {
      await invalidateAllAggregateCaches();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t('aliases.saveFailed'));
    },
  });

  return (
    <AppDrawer
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={mode === 'create' ? t('aliases.modalCreateTitle') : t('aliases.modalEditTitle')}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm hover:bg-muted"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={
              saveMutation.isPending ||
              !displayName ||
              (mode === 'create' && !sourceText) ||
              !!exactMatch
            }
            onClick={() => {
              setError(null);
              saveMutation.mutate();
            }}
            className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {saveMutation.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">
            {t('aliases.fieldSourceText')}
          </span>
          <input
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            placeholder="ANGODA PTY LTD"
            disabled={mode === 'edit'}
            className="w-full rounded-md border px-3 py-2 text-sm disabled:bg-muted disabled:text-muted-foreground"
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            {t('aliases.fieldSourceTextHint')}
          </span>
        </label>

        {/* Exact source_key clash — creating would 409. Surface up-front
            with TWO options:
            (a) Apply the existing rule to current+matching receipts (runs
                /aliases/:id/backfill) — solves the "I'm on a receipt that
                should've been rewritten but wasn't" case the user actually
                lands here for most of the time;
            (b) Open the existing rule for inspect/edit.
            The full create form below is suppressed since creating a new
            rule with the same key is impossible. */}
        {exactMatch && (
          <div className="rounded-md border border-amber-400 bg-amber-50 p-3 text-sm dark:border-amber-600 dark:bg-amber-950/30">
            <div className="font-medium text-amber-900 dark:text-amber-200">
              {t('aliases.exactExistsTitle')}
            </div>
            <div className="mt-1 text-xs text-amber-800 dark:text-amber-300">
              <span className="font-mono">{exactMatch.source_key}</span>
              {' → '}
              <span className="font-medium">{exactMatch.display_name}</span>
              {exactMatch.display_name_zh ? ` · ${exactMatch.display_name_zh}` : ''}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {preview && preview.count > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    applyExistingMutation.mutate();
                  }}
                  disabled={applyExistingMutation.isPending}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50 dark:bg-amber-500 dark:hover:bg-amber-400"
                >
                  {applyExistingMutation.isPending
                    ? t('aliases.applyingExisting')
                    : t('aliases.applyExistingCta', { count: preview.count })}
                </button>
              )}
              <Link
                to={`/aliases#alias-${exactMatch.id}`}
                onClick={onClose}
                className="rounded-md border border-amber-400 bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                {t('aliases.exactExistsCta')}
              </Link>
            </div>
          </div>
        )}

        {/* Related-but-not-exact aliases: tap to clone target onto this new
            rule. Solves the "abc hornsby" / "abc hornsby shop" friction
            without forcing the user to retype display_name + zh + category. */}
        {!exactMatch && related.length > 0 && (
          <div className="rounded-md border bg-muted/40 p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              {t('aliases.relatedHint')}
            </div>
            <ul className="space-y-1.5">
              {related.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => reuseTargetFrom(a)}
                    className="w-full rounded-md border bg-background p-2 text-left text-sm hover:bg-muted"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="line-clamp-2 font-medium">{a.display_name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {t('aliases.appliedCount', { count: a.apply_count })}
                      </span>
                    </div>
                    <div className="mt-0.5 line-clamp-2 break-all font-mono text-xs text-muted-foreground">
                      {a.source_key}
                      {a.display_name_zh ? ` · ${a.display_name_zh}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Hide the create-form fields + preview block when an exact match
            exists — the warning card above already exposes the only
            actionable paths (apply / open existing). Showing the empty
            form below the warning just confused users into thinking they
            could still submit. */}
        {!exactMatch && (
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">{t('aliases.fieldDisplayName')}</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Agoda"
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
        )}

        {!exactMatch && (
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">
              {t('aliases.fieldDisplayNameZh')}
            </span>
            <input
              value={displayNameZh}
              onChange={(e) => setDisplayNameZh(e.target.value)}
              placeholder="阿哥达"
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
        )}

        {!exactMatch && (
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">
              {t('aliases.fieldDefaultCategory')}
            </span>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="">{t('aliases.fieldDefaultCategoryNone')}</option>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.icon ? `${c.icon} ` : ''}
                  {c.name}
                  {c.name_zh ? ` / ${c.name_zh}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {!exactMatch && preview && preview.count > 0 && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
            <div className="mb-2 font-medium">
              {t('aliases.previewMatches', { count: preview.count })}
            </div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {preview.samples.map((s) => (
                <li key={s.id}>
                  · {s.receipt_date ?? '—'}  {s.merchant ?? '—'}  ${s.total_amount.toFixed(2)}
                </li>
              ))}
            </ul>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={backfill}
                onChange={(e) => setBackfill(e.target.checked)}
              />
              <span>{t('aliases.previewBackfillCheckbox', { count: preview.count })}</span>
            </label>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </AppDrawer>
  );
}
